import React from "react";

/* The full brain catalog for marketing pages — 12 brains across 8 providers.
   Keep in sync with /pitch and backend `providers.BRAINS`. */
export const BRAIN_CATALOG = [
  { id: "gpt-5-1",         name: "GPT-5.1",        provider: "OpenAI",     color: "#10B981", tag: "Reasoning" },
  { id: "gpt-4o-mini",     name: "GPT-4o mini",    provider: "OpenAI",     color: "#10B981", tag: "Fast & cheap" },
  { id: "claude-sonnet-46",name: "Sonnet 4.6",     provider: "Anthropic",  color: "#D97706", tag: "Writing" },
  { id: "claude-haiku-45", name: "Haiku 4.5",      provider: "Anthropic",  color: "#D97706", tag: "Speed" },
  { id: "gemini-25-pro",   name: "Gemini 2.5 Pro", provider: "Google",     color: "#3B82F6", tag: "Long context" },
  { id: "gemini-flash",    name: "Flash-lite",     provider: "Google",     color: "#3B82F6", tag: "Cheapest" },
  { id: "deepseek-v3",     name: "DeepSeek V3",    provider: "DeepSeek",   color: "#6366F1", tag: "Math & code" },
  { id: "qwen-72b",        name: "Qwen 2.5 72B",   provider: "Alibaba",    color: "#EF4444", tag: "Multilingual" },
  { id: "kimi-k2",         name: "Kimi K2",        provider: "Moonshot",   color: "#EC4899", tag: "Storyteller" },
  { id: "llama-33",        name: "Llama 3.3 70B",  provider: "Meta",       color: "#F59E0B", tag: "Open weights" },
  { id: "mistral-large",   name: "Mistral Large",  provider: "Mistral",    color: "#8B5CF6", tag: "European" },
  { id: "grok-2",          name: "Auto Brain",     provider: "Router",     color: "#0F172A", tag: "Auto-pick" },
];

export function BrainChip({ brain, size = "md" }) {
  const pad = size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm";
  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full border-2 border-slate-900 bg-white font-heading font-bold ${pad} whitespace-nowrap`}
      data-testid={`brain-chip-${brain.id}`}
    >
      <span
        className="inline-block w-2.5 h-2.5 rounded-full border border-slate-900"
        style={{ backgroundColor: brain.color }}
      />
      <span>{brain.name}</span>
      <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{brain.provider}</span>
    </div>
  );
}

/* Infinite horizontal marquee — brains stream across the screen. */
export function BrainMarquee({ speed = 40 }) {
  const strip = [...BRAIN_CATALOG, ...BRAIN_CATALOG];
  return (
    <div className="overflow-hidden border-y-2 border-slate-900 py-4 bg-white" data-testid="brain-marquee">
      <div
        className="flex gap-3 whitespace-nowrap animate-marquee"
        style={{ width: "200%", animationDuration: `${speed}s` }}
      >
        {strip.map((b, i) => (
          <BrainChip key={i} brain={b} />
        ))}
      </div>
      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .animate-marquee { animation: marquee linear infinite; }
      `}</style>
    </div>
  );
}

/* Grouped constellation — brains grouped by provider (for the About page). */
export function BrainConstellation() {
  const byProvider = BRAIN_CATALOG.reduce((acc, b) => {
    (acc[b.provider] = acc[b.provider] || []).push(b);
    return acc;
  }, {});
  const order = ["OpenAI", "Anthropic", "Google", "DeepSeek", "Alibaba", "Moonshot", "Meta", "Mistral", "Router"];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="brain-constellation">
      {order.filter((p) => byProvider[p]).map((provider) => (
        <div key={provider} className="brut-card p-5 bg-white" data-testid={`provider-${provider.toLowerCase()}`}>
          <div className="flex items-center gap-2 mb-3">
            <span
              className="w-3 h-3 rounded-full border-2 border-slate-900"
              style={{ backgroundColor: byProvider[provider][0].color }}
            />
            <div className="font-heading font-bold text-lg">{provider}</div>
          </div>
          <div className="space-y-1.5">
            {byProvider[provider].map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="font-heading font-bold">{b.name}</span>
                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                  {b.tag}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
